/**
 * Injects the theme's product card "Add to cart" button into Boost Commerce
 * product cards. Boost renders (and re-renders on filter/sort/paginate) its
 * grid client-side, so cards are picked up with a MutationObserver.
 *
 *   - single available variant → adds to cart and opens the Rebuy Smart Cart
 *   - multiple variants        → "Choose options", links to the product page
 *   - nothing available        → disabled "Sold out"
 */


(function () {
  const CARD_SELECTOR = '.boost-sd__product-item';
  const MOUNT_SELECTOR = '.boost-sd__product-info-wrapper';
  const LINK_SELECTOR = 'a.boost-sd__product-link[href*="/products/"]';
  const LIST_CTA_SELECTOR = '.boost-sd__product-item-list-view-layout-cta-buttons';
  const QUICK_VIEW_IMAGE_SELECTOR = '.boost-sd__quick-view img';
  const QUICK_VIEW_IMAGE_WIDTH = 900;

  const template = document.getElementById('BoostCardAtcTemplate');
  if (!template) return;

  const strings = window.boostCardAtcStrings || {};
  const productCache = new Map();

  function productPath(card) {
    const link = card.querySelector(LINK_SELECTOR);
    if (!link) return null;
    const url = new URL(link.href, window.location.origin);
    const match = url.pathname.match(/^(.*?\/products\/[^/]+)/);
    return match ? match[1] : null;
  }

  function fetchProduct(path) {
    if (!productCache.has(path)) {
      productCache.set(
        path,
        fetch(`${path}.js`, { headers: { Accept: 'application/json' } }).then((response) => {
          if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
          return response.json();
        })
      );
    }
    return productCache.get(path);
  }

  function setLabel(button, text) {
    const label = button.querySelector('[data-atc-text]');
    if (label) label.textContent = text;
  }

  function hasQuantityRules(variant) {
    const rule = variant && variant.quantity_rule;
    return Boolean(rule && (rule.min > 1 || rule.max != null || rule.increment > 1));
  }

  async function openCart() {
    const rebuy = window.Rebuy;
    if (rebuy && rebuy.SmartCart && typeof rebuy.SmartCart.show === 'function') {
      if (rebuy.Cart && typeof rebuy.Cart.fetchCart === 'function') {
        try {
          await rebuy.Cart.fetchCart();
        } catch (error) {
          // Smart Cart refreshes itself on open; ignore and still show it.
        }
      }
      rebuy.SmartCart.show();
      return;
    }
    window.location.href = (window.routes && window.routes.cart_url) || '/cart';
  }

  async function addToCart(button, variantId) {
    const spinner = button.querySelector('.loading__spinner');
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    if (spinner) spinner.classList.remove('hidden');

    try {
      const response = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Add to cart failed');

      const label = button.querySelector('.qt-product-card__atc-label');
      const added = button.querySelector('.quick-add__added-message');
      if (label && added) {
        label.classList.add('hidden');
        added.classList.remove('hidden');
        button.classList.add('quick-add__submit--added');
        setTimeout(() => {
          label.classList.remove('hidden');
          added.classList.add('hidden');
          button.classList.remove('quick-add__submit--added');
        }, 2000);
      }

      await openCart();
    } catch (error) {
      console.error('[boost-card-atc]', error);
    } finally {
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  // List view: sit in Boost's CTA column above "Quick view". Grid view: under the price.
  function placeButton(card, wrapper) {
    const listCta = card.querySelector(LIST_CTA_SELECTOR);
    const mount = listCta || card.querySelector(MOUNT_SELECTOR);
    if (!mount) return false;
    if (listCta) {
      if (wrapper.parentElement !== listCta || listCta.firstElementChild !== wrapper) listCta.prepend(wrapper);
    } else if (wrapper.parentElement !== mount) {
      mount.appendChild(wrapper);
    }
    return true;
  }

  function enhanceCard(card) {
    const existing = card.querySelector('.qt-boost-card-atc');
    if (existing) {
      placeButton(card, existing);
      return;
    }
    const path = productPath(card);
    if (!path) return;

    const wrapper = template.content.firstElementChild.cloneNode(true);
    const button = wrapper.querySelector('button');
    button.disabled = true;
    if (!placeButton(card, wrapper)) return;
    card.classList.add('qt-boost-card--has-atc');

    fetchProduct(path)
      .then((product) => {
        const variants = product.variants || [];
        const available = variants.filter((variant) => variant.available);

        if (!product.available || !available.length) {
          setLabel(button, strings.soldOut);
          return;
        }

        button.disabled = false;

        if (variants.length > 1 || hasQuantityRules(available[0])) {
          setLabel(button, strings.chooseOptions);
          button.addEventListener('click', () => {
            window.location.href = path;
          });
          return;
        }

        setLabel(button, strings.addToCart);
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (button.getAttribute('aria-disabled') === 'true') return;
          addToCart(button, available[0].id);
        });
      })
      .catch(() => {
        // Fall back to the product page if the product can't be loaded.
        button.disabled = false;
        setLabel(button, strings.chooseOptions);
        button.addEventListener('click', () => {
          window.location.href = path;
        });
      });
  }

  // Boost's quick view loads full-size originals (1200–1600px); request a sized CDN image instead.
  function resizeQuickViewImages(root) {
    root.querySelectorAll(QUICK_VIEW_IMAGE_SELECTOR).forEach((img) => {
      if (img.dataset.qtResized) return;
      img.dataset.qtResized = 'true';
      const src = img.getAttribute('src');
      if (!src || !src.includes('cdn.shopify.com') || /[?&]width=/.test(src)) return;
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', QUICK_VIEW_IMAGE_WIDTH);
      img.removeAttribute('srcset');
      img.setAttribute('src', url.toString());
    });
  }

  function scan(root) {
    if (root.matches && root.matches(CARD_SELECTOR)) enhanceCard(root);
    if (root.querySelectorAll) {
      root.querySelectorAll(CARD_SELECTOR).forEach(enhanceCard);
      resizeQuickViewImages(root);
    }
  }

  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      scan(document.body);
    });
  });

  scan(document.body);
  observer.observe(document.body, { childList: true, subtree: true });
})();
